
/** `uiphasecurtain.py:11` -- `self.speed = 0.1`, one step per frame. */
export const CURTAIN_STEP = 0.1;

/** The three facts the release rule is written over. */
export interface CurtainReadiness {
  /** The entity id the server gave this client, or 0/-1 before it has. */
  readonly selfId: number;
  /** Entity snapshots applied so far (`Connection.snapshotsApplied`). */
  readonly snapshotsApplied: number;
  /** Mesh-body draws for `selfId` on the last completed paint pass
   *  (`Renderer.renderedCharacterParts(selfId)?.bodyDraws`), 0 when the frame
   *  fell back to the silhouette or drew nothing at all. */
  readonly selfBodyDraws: number;
}

export function curtainReleased(state: CurtainReadiness): boolean {
  return state.selfId !== 0 && state.snapshotsApplied > 0 && state.selfBodyDraws > 0;
}

const STYLE_ID = "phase-curtain-style";

const STYLE = `
.phase-curtain {
  position: fixed;
  inset: 0;
  z-index: 400;
  background: #000;
  pointer-events: none;
}
.phase-curtain[hidden] { display: none !important; }
`;

export class PhaseCurtain {
  readonly element: HTMLElement;
  private alpha = 1;
  private lowering = false;

  constructor(doc: Document, mount: HTMLElement = doc.body) {
    if (doc.getElementById(STYLE_ID) === null) {
      const style = doc.createElement("style");
      style.id = STYLE_ID;
      style.textContent = STYLE;
      doc.head.append(style);
    }
    const el = doc.createElement("div");
    el.className = "phase-curtain";
    el.setAttribute("aria-hidden", "true");
    el.style.opacity = "1";
    this.element = el;
    mount.append(el);
  }

  /** Alpha now: 1 fully black, 0 gone. */
  get opacity(): number {
    return this.alpha;
  }

  /** True once the curtain is off the screen and can never come back. */
  get down(): boolean {
    return this.lowering && this.alpha <= 0;
  }

  /** `FadeIn` -- start lowering. Idempotent, so a frame loop may call it on
   *  every frame the release condition holds. */
  release(): void {
    this.lowering = true;
  }

  /** One `OnUpdate` step. Returns the alpha it left behind. */
  step(): number {
    if (!this.lowering || this.alpha <= 0) return this.alpha;
    this.alpha = Math.max(0, this.alpha - CURTAIN_STEP);
    this.element.style.opacity = String(this.alpha);
    if (this.alpha <= 0) this.element.hidden = true;
    return this.alpha;
  }
}
